import { useAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useCustomToast from '@/hooks/toasts'
import { searchAtom } from '@/store/app/AppAtom'
import {
    chatIdAtom,
    conversationsListItemAtom,
    deleteConversationAtom,
    isServerRespondingAtom,
    mobileDrawerAtom,
    postChatSettings,
    toggleMobiledrawer,
} from '@/store/gigiChat/ChatAtom'
import { pinnedChatsAtom } from '@/store/gigiChat/PinAtom.ts'
import {
    mobileChatHistoryDrawerAtom,
    mobileDocumentsHistroyDrawerAtom,
    mobileTranslateHistroyDrawerAtom,
    toggleChatHistoryMobileDrawer,
    toggleDocumentsHistoryMobileDrawer,
    toggleTranslateHistoryMobileDrawer,
} from '@/store/mobile/MobileAtom'
import { langsAtom, selectedLanguagesAtom } from '@/store/translate'
import { IConversationListItem } from '@/types/gigiChat/Conversation'
import { IConversationSettings, TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'
import { groupByRelativeDate } from '@/utils/chat'

import ConversationList from './ConversationList'

const Container = ({ processingStrategy }: { processingStrategy?: TProcessingStrategy }) => {
    const [isOpen] = useAtom(mobileDrawerAtom)
    const [isDocumentsHistoryDrawerOpen] = useAtom(mobileDocumentsHistroyDrawerAtom)
    const [isChatHistoryDrawerOpen] = useAtom(mobileChatHistoryDrawerAtom)
    const [isTranslateDrawerOpen] = useAtom(mobileTranslateHistroyDrawerAtom)
    const { t } = useTranslation()
    const [chatId] = useAtom<string>(chatIdAtom)
    const [isServerResponding] = useAtom<boolean>(isServerRespondingAtom)
    const [, mutate] = useAtom(deleteConversationAtom)
    const [data] = useAtom<Array<IConversationListItem>>(conversationsListItemAtom)
    const [filteredData, setFilteredData] = useState<Array<IConversationListItem>>([])
    const [wordToSearch] = useAtom<string>(searchAtom)
    const [pinnedChats] = useAtom(pinnedChatsAtom)
    const [langs] = useAtom(langsAtom)
    const [, setSelectedLangs] = useAtom(selectedLanguagesAtom)

    const [addToast] = useCustomToast()

    const group = useCallback(() => {
        return groupByRelativeDate(filteredData, pinnedChats)
    }, [filteredData, pinnedChats])

    useEffect(() => {
        if (data) {
            if (wordToSearch) {
                const filteredList = data.filter((item) => {
                    const title = item.settings.title?.toLowerCase() ?? ''

                    if (processingStrategy === 'Translation' && item.questionDisplayData?.displayData?.fileNames) {
                        const files = item.questionDisplayData.displayData.fileNames.filter((doc) =>
                            doc.toLowerCase().includes(wordToSearch.toLowerCase()),
                        )
                        return title.includes(wordToSearch.toLowerCase()) || files.length > 0
                    }

                    return title.includes(wordToSearch.toLowerCase())
                })

                setFilteredData(filteredList)
            } else {
                setFilteredData(data)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, wordToSearch])

    if (!Array.isArray(data)) {
        return null
    }

    const onDelete = (conversationId: string) => {
        void mutate([{ chatID: conversationId, processingStrategy }]).then(() => {
            addToast('success', t('chat.deleteConversation.toast.success'))
        })
    }

    const onClick = (settings: IConversationSettings) => {
        // Prevent clicking on a conversation while the server is still responding
        if (isServerResponding) {
            return false
        }

        if (processingStrategy && processingStrategy !== 'DocAnalysis') {
            setSelectedLangs(langs.filter((language) => settings.processingStrategyTranslation.languages.includes(language.languageCode)))
        }

        if (isOpen) {
            toggleMobiledrawer()
        }

        if (isChatHistoryDrawerOpen) toggleChatHistoryMobileDrawer()
        if (isDocumentsHistoryDrawerOpen) toggleDocumentsHistoryMobileDrawer()
        if (isTranslateDrawerOpen) toggleTranslateHistoryMobileDrawer()
    }

    const saveTitle = (chatId: string, localSettings: IConversationSettings) => {
        return postChatSettings(chatId, localSettings, true)
    }

    return (
        <ConversationList
            conversations={group()}
            onDelete={onDelete}
            onClick={onClick}
            activeChatId={chatId}
            saveTitle={saveTitle}
            processingStrategy={processingStrategy}
        />
    )
}

export default Container
